import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from 'react-select/async';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { sports } from '../../../../components/Util/enum';
import { useFederations } from '../../../../../hooks/useFederations';
import { useProfile } from '../../../../../hooks/useProfile';

const LISTA_TESSERATI = '/struttura/tesserati/list';

const Obbligatorio = () => <span className="text-danger ms-1">*</span>;

export const NuovoTesseramento = () => {

	const navigate = useNavigate();
	const { federations } = useFederations();
	const { profile: profileData } = useProfile({ autoFetch: true });

	const [loadingShowed, setLoadingShowed] = useState(false);
	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');
	const [successShowed, setSuccessShowed] = useState(false);

	// Player (comune alle due modalità)
	const [selectedUser, setSelectedUser] = useState(null);
	const [perJunior, setPerJunior] = useState(false);
	const [juniors, setJuniors] = useState([]);
	const [selectedJunior, setSelectedJunior] = useState(null);

	// Vendita tessera
	const [prodottiTessera, setProdottiTessera] = useState([]);
	const [selectedProdotto, setSelectedProdotto] = useState(null);

	// Inserimento manuale
	const [enabledSports, setEnabledSports] = useState([]);
	const [selectedSport, setSelectedSport] = useState(null);
	const [availableEnti, setAvailableEnti] = useState([]);
	const [selectedEnte, setSelectedEnte] = useState(null);
	const [dateStart, setDateStart] = useState('');
	const [dateEnd, setDateEnd] = useState('');

	const getToken = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		return JSON.parse(tokenDetailsString).idToken;
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	useEffect(() => {
		if (profileData && profileData.club && profileData.club.sports) {
			const clubSports = profileData.club.sports.map(s => s.sportType);
			setEnabledSports(sports.filter(element => clubSports.includes(element.value)));
		}
	}, [profileData]);

	// Solo i prodotti che conferiscono lo status di tesserato: gli altri
	// abbonamenti non c'entrano nulla con il registro.
	useEffect(() => {
		const carica = async () => {
			const token = getToken();
			if (!token) return;

			const apiClient = new MyApiClient();
			const risultato = await apiClient.getBookingSubscriptions(token);
			if (!risultato || risultato.error) return;

			setProdottiTessera(
				risultato
					.filter(p => p.status === 'PURCHASABLE' && p.grants_federated_status)
					.map(p => ({
						value: p.id,
						label: p.name,
						prezzo: p.standard_price
					}))
			);
		};
		carica();
	}, []);

	const caricaJuniors = useCallback(async (playerId) => {
		const token = getToken();
		if (!token || !playerId) return;

		const apiClient = new MyApiClient();
		const risultato = await apiClient.getJuniorsByParent(token, playerId);
		const elenco = Array.isArray(risultato) ? risultato : (risultato?.items || []);
		// L'endpoint restituisce givenName/familyName, non un `name` gia' composto.
		setJuniors(elenco.map(j => ({
			value: j.userId,
			label: [j.givenName, j.familyName].filter(Boolean).join(' ') || j.userId
		})));
	}, []);

	const handleUserSelect = (opzione) => {
		setSelectedUser(opzione);
		setSelectedJunior(null);
		setJuniors([]);
		if (opzione) caricaJuniors(opzione.value);
	};

	const loadUserOptions = async (inputValue) => {
		if (inputValue.length < 3) return [];

		const token = getToken();
		if (!token) return [];

		const apiClient = new MyApiClient();
		const playersData = await apiClient.getProfilesByFilters(token, 10000, inputValue, 'IDS_PLAY');
		if (playersData && !playersData.error) {
			return playersData.items.map(element => ({ value: element.userId, label: element.name }));
		}
		return [];
	};

	const handleSportChange = (opzione) => {
		setSelectedSport(opzione);
		setSelectedEnte(null);

		if (!opzione || !federations.length) {
			setAvailableEnti([]);
			return;
		}

		// Organismi configurati dalla struttura per questo sport
		const clubSport = profileData?.club?.sports?.find(s => s.sportType === opzione.value);
		const clubOrganisms = clubSport?.sportPromotionBody || [];

		setAvailableEnti(
			federations
				.filter(fed => clubOrganisms.includes(fed.code))
				.map(fed => ({ value: fed.code, label: `${fed.code} - ${fed.description}` }))
		);
	};

	/** userId a cui intestare la tessera: il junior se richiesto, altrimenti il player. */
	const destinatario = () => (perJunior ? selectedJunior?.value : selectedUser?.value);

	const controllaPlayer = () => {
		if (!selectedUser) {
			mostraErrore("Seleziona prima il player.");
			return false;
		}
		if (perJunior && !selectedJunior) {
			mostraErrore("Seleziona il junior a cui intestare la tessera.");
			return false;
		}
		return true;
	};

	// La vendita passa dal flusso completo: lì si raccolgono consensi e dati di
	// fatturazione, obbligatori per emettere il documento fiscale. Qui si sceglie
	// il prodotto e lo si porta di là già selezionato.
	const inviaOrdine = () => {
		if (!controllaPlayer()) return;
		if (!selectedProdotto) {
			mostraErrore("Seleziona il tipo di prodotto da vendere.");
			return;
		}
		navigate(`/struttura/booking/userSubscriptions/new?subscriptionId=${selectedProdotto.value}`);
	};

	const salvaManuale = async () => {
		if (!controllaPlayer()) return;
		if (!selectedSport) {
			mostraErrore("Seleziona lo sport della tessera.");
			return;
		}
		if (!selectedEnte) {
			mostraErrore("Seleziona l'organismo sportivo che ha rilasciato la tessera.");
			return;
		}
		if (!dateEnd) {
			mostraErrore("La data di fine validità deve essere valorizzata.");
			return;
		}
		if (dateStart && dateStart > dateEnd) {
			mostraErrore("La data di inizio non può essere successiva alla data di fine.");
			return;
		}

		const token = getToken();
		if (!token) return;

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.addPlayerLicense(token, {
			userId: destinatario(),
			sportType: selectedSport.value,
			federationCode: selectedEnte.value,
			...(dateStart && { dateStart }),
			expiration: dateEnd,
			asdssdName: profileData?.club?.name
		});
		setLoadingShowed(false);

		if (risultato && risultato.error) {
			mostraErrore("Si è verificato un errore durante il salvataggio: " + risultato.error);
			return;
		}
		setSuccessShowed(true);
	};

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={successShowed} centered>
				<Modal.Header>
					<Modal.Title>Operazione completata</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Row><Col>Il tesserato è stato inserito correttamente.</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-between">
					<Button className="btn btn-primary btn-add" onClick={() => navigate(LISTA_TESSERATI)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<div style={{ maxWidth: '920px' }}>
				<div className="card">
					<div className="card-header">
						<h4 className="card-title">Seleziona il player</h4>
					</div>
					<div className="card-body">
						<Form.Label>Cerca player<Obbligatorio /></Form.Label>
						<AsyncSelect
							value={selectedUser}
							onChange={handleUserSelect}
							loadOptions={loadUserOptions}
							placeholder="Inizia a digitare per cercare un player (minimo 3 caratteri)…"
							noOptionsMessage={({ inputValue }) =>
								inputValue.length < 3
									? "Digita almeno 3 caratteri per iniziare la ricerca"
									: "Nessun player trovato"
							}
						/>
						<div className="text-muted mt-2" style={{ fontSize: '13px' }}>
							Cerca per nome o cognome tra i player della Struttura.
						</div>

						<Form.Check
							type="checkbox"
							id="tessera-junior"
							className="mt-4"
							label="Seleziona Junior"
							checked={perJunior}
							onChange={(e) => { setPerJunior(e.target.checked); setSelectedJunior(null); }}
							disabled={!selectedUser}
						/>
						{perJunior && (
							<div className="mt-3">
								<Form.Label>Junior<Obbligatorio /></Form.Label>
								<Select
									value={selectedJunior}
									onChange={setSelectedJunior}
									options={juniors}
									placeholder={juniors.length ? "Seleziona junior…" : "Questo player non ha junior collegati"}
									isDisabled={!juniors.length}
								/>
							</div>
						)}
					</div>
				</div>

				<h4 className="card-title" style={{ margin: '22px 0 12px' }}>Scegli come tesserare il player</h4>

				<Row>
					<Col lg={6} className="mb-4 d-flex">
						<div className="card w-100 mb-0">
							<div className="card-header">
								<h4 className="card-title">Vendita tessera</h4>
							</div>
							<div className="card-body d-flex flex-column">
								<p className="text-muted">Acquista una nuova tessera per il player.</p>

								<div className="mb-3">
									<Form.Label>Tipo di prodotto<Obbligatorio /></Form.Label>
									<Select
										value={selectedProdotto}
										onChange={setSelectedProdotto}
										options={prodottiTessera}
										placeholder={prodottiTessera.length ? "Seleziona prodotto…" : "Nessun prodotto tessera configurato"}
										isDisabled={!prodottiTessera.length}
									/>
								</div>

								<div className="sid-price-box">
									<span>Prezzo</span>
									<strong>
										{selectedProdotto && selectedProdotto.prezzo !== undefined
											? `€ ${Number(selectedProdotto.prezzo).toFixed(2)}`
											: '—'}
									</strong>
								</div>

								<div className="text-muted mt-3" style={{ fontSize: '13px' }}>
									Date di validità, pagamento e dati di fatturazione si completano nella
									pagina di vendita: sono necessari per emettere il documento fiscale.
								</div>

								<div className="flex-grow-1" />
								<div className="d-flex justify-content-end gap-2 mt-4">
									<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_TESSERATI)}>Annulla</Button>
									<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={inviaOrdine}>Invia ordine</Button>
								</div>
							</div>
						</div>
					</Col>

					<Col lg={6} className="mb-4 d-flex">
						<div className="card w-100 mb-0">
							<div className="card-header">
								<h4 className="card-title">Inserimento manuale</h4>
							</div>
							<div className="card-body d-flex flex-column">
								<p className="text-muted">Registra manualmente una tessera già esistente presso un organismo sportivo.</p>

								<div className="mb-3">
									<Form.Label>Sport<Obbligatorio /></Form.Label>
									<Select
										value={selectedSport}
										onChange={handleSportChange}
										options={enabledSports}
										placeholder="Seleziona sport…"
									/>
								</div>

								<div className="mb-3">
									<Form.Label>Organismo sportivo<Obbligatorio /></Form.Label>
									<Select
										value={selectedEnte}
										onChange={setSelectedEnte}
										options={availableEnti}
										placeholder="Seleziona organismo…"
										isDisabled={!selectedSport}
									/>
								</div>

								<Row>
									<Col md={6} className="mb-3">
										<Form.Label>Data inizio</Form.Label>
										<Form.Control type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
									</Col>
									<Col md={6} className="mb-3">
										<Form.Label>Data fine<Obbligatorio /></Form.Label>
										<Form.Control type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
									</Col>
								</Row>

								<div className="flex-grow-1" />
								<div className="d-flex justify-content-end gap-2 mt-4">
									<Button className="btn btn-add sid-btn-secondary" onClick={() => navigate(LISTA_TESSERATI)}>Annulla</Button>
									<Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salvaManuale}>Salva</Button>
								</div>
							</div>
						</div>
					</Col>
				</Row>
			</div>
		</>
	);
};

export default NuovoTesseramento;
